import React from 'react';
import { Link } from 'react-router-dom';
import { Brain, Heart, Zap, Users, Target, MessageCircle, Sparkles, Flame, ArrowRight } from 'lucide-react';

const TrainingsPage = () => {
  const trainings = [
    {
      icon: Brain,
      title: 'Emocinio intelekto mokymai',
      description: 'Padėti jums ir jūsų komandai geriau pažinti save ir vienas kitą bei stiprinti tarpusavio ryšį',
      duration: '5 susitikimai po 4 val.',
      link: '/mokymai/emocinis-intelektas',
      color: 'bg-[#4CAF50]',
      lightColor: 'bg-[#E8F5E9]'
    },
    {
      icon: Heart,
      title: 'Emocinės sveikatos mokymai',
      description: 'Padėti vadovams suprasti jų vaidmenį kuriant emocinę sveikatą komandoje',
      duration: '8 akademinės valandos',
      link: '/mokymai/emocinė-sveikata',
      color: 'bg-[#FF6B6B]',
      lightColor: 'bg-[#FFEBEE]'
    },
    {
      icon: Zap,
      title: 'Streso valdymo mokymai',
      description: 'Išmokti atpažinti streso požymius ir efektyviai jį valdyti',
      duration: '4-8 akademinės valandos',
      link: '/mokymai/streso-valdymas',
      color: 'bg-[#FF9F43]',
      lightColor: 'bg-[#FFF3E0]'
    },
    {
      icon: Users,
      title: 'Sudėtingų asmenybių valdymas',
      description: 'Išmokti efektyviai bendrauti net pačiose sudėtingiausiose situacijose',
      duration: '4-8 akademinės valandos',
      link: '/mokymai/sudėtingos-asmenybės',
      color: 'bg-[#9B59B6]',
      lightColor: 'bg-[#F3E5F5]'
    },
    {
      icon: Flame,
      title: 'Perdegimo prevencija',
      description: 'Atpažinti perdegimo požymius ir išmokti priemonių jam įveikti',
      duration: '4-8 akademinės valandos',
      link: '/mokymai/perdegimas',
      color: 'bg-[#F59E0B]',
      lightColor: 'bg-[#FFFDE7]'
    },
    {
      icon: Sparkles,
      title: 'Viešasis kalbėjimas',
      description: 'Įgyti pasitikėjimo kalbant prieš auditoriją',
      duration: '4-8 akademinės valandos',
      link: '/mokymai/viešasis-kalbėjimas',
      color: 'bg-[#5B9BD5]',
      lightColor: 'bg-[#E3F2FD]'
    },
    {
      icon: MessageCircle,
      title: 'Konfliktų valdymas',
      description: 'Išmokti konstruktyviai spręsti konfliktus ir gerinti bendravimą',
      duration: '4-8 akademinės valandos',
      link: '/mokymai/konfliktų-valdymas',
      color: 'bg-[#FF6B6B]',
      lightColor: 'bg-[#FFEBEE]'
    }
  ];

  return (
    <div data-testid="trainings-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center space-x-2 bg-[#E8F5E9] text-[#4CAF50] px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Target className="w-4 h-4" />
              <span>Mokymai organizacijoms</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-['Poppins'] font-bold leading-tight text-[#2D3748] mb-6">
              Mokymų <span className="text-[#4CAF50]">Programos</span>
            </h1>
            <p className="text-lg text-[#718096] leading-relaxed">
              Profesionalūs, praktiniai mokymai jūsų komandai. Kiekviena programa pritaikoma pagal organizacijos poreikius.
            </p>
          </div>
        </div>
      </section>

      {/* Trainings Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {trainings.map((training, index) => {
              const Icon = training.icon;
              return (
                <Link
                  to={training.link}
                  key={index}
                  data-testid={`training-card-${index}`}
                  className="bg-white p-6 rounded-3xl border-2 border-slate-100 hover:border-transparent hover:shadow-xl transition-all group"
                >
                  <div className={`w-14 h-14 ${training.lightColor} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-7 h-7 ${training.color.replace('bg-', 'text-')}`} />
                  </div>
                  <h3 className="text-xl font-['Poppins'] font-semibold mb-2 text-[#2D3748]">
                    {training.title}
                  </h3>
                  <p className="text-[#718096] text-sm mb-4 leading-relaxed">
                    {training.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`${training.lightColor} ${training.color.replace('bg-', 'text-')} text-xs px-3 py-1 rounded-full font-medium`}>
                      {training.duration}
                    </span>
                    <ArrowRight className={`w-5 h-5 ${training.color.replace('bg-', 'text-')} group-hover:translate-x-1 transition-transform`} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="bg-[#FAFAF8] py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748] mb-8 text-center">
            Kodėl rinktis šiuos mokymus?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-[#E8F5E9] rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-[#4CAF50] mb-2">10+</div>
              <p className="text-[#718096]">Metų patirtis</p>
            </div>
            <div className="bg-[#FFF3E0] rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-[#FF9F43] mb-2">100+</div>
              <p className="text-[#718096]">Apmokytų grupių</p>
            </div>
            <div className="bg-[#E3F2FD] rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-[#5B9BD5] mb-2">3</div>
              <p className="text-[#718096]">Kalbos</p>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100">
            <p className="text-lg text-[#718096] leading-relaxed text-center">
              Kiekvienas mokymas yra unikalus ir pritaikomas pagal auditorijos poreikius. 
              Per 10+ metų vedžiau mokymus labai skirtingoms komandoms ir organizacijoms – 
              nuo mažų startuolių iki tarptautinių korporacijų kaip IKEA, McDonald's, Girteka.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#4CAF50] py-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-white mb-6">
            Ieškote mokymų savo komandai?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Susisiekite ir aptarkime, kokia programa geriausiai atitiktų jūsų poreikius
          </p>
          <Link
            to="/kontaktai"
            data-testid="cta-contact-button"
            className="inline-flex items-center bg-white text-[#4CAF50] hover:bg-slate-100 rounded-full px-8 py-4 font-semibold transition-colors"
          >
            Gauti pasiūlymą
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TrainingsPage;
